import { AxiosError } from "axios";

import { FlowGroup, FlowGroupForCount } from "src/api/flowTypes";
import {
  CustomConnectionNode,
  IntegrationNode,
  InboundWebhookConnectionNode,
  Node,
  ManualReviewNode,
  DatabaseConnectionNode,
  FlowNode,
  DatasetMockableNodes,
  IntegrationNodes,
  LoopNode,
  ParentNode,
  NodeRunStateV2,
  NodeHistoricalRunStateV2,
  ManifestConnectionNode,
  AiNodeV2,
  ReadEntityNode,
  WriteEntityNode,
} from "src/constants/NodeDataTypes";
import { NODE_TYPE } from "src/constants/NodeTypes";

export const isIntegrationNode = (node: Node): node is IntegrationNode =>
  node.type === NODE_TYPE.INTEGRATION_NODE;

export const isInboundWebhookConnectionNode = (
  node: Node,
): node is InboundWebhookConnectionNode =>
  node.type === NODE_TYPE.WEBHOOK_CONNECTION_NODE;

export const isDatabaseConnectionNode = (
  node: Node,
): node is DatabaseConnectionNode =>
  node.type === NODE_TYPE.SQL_DATABASE_CONNECTION_NODE;

export const isCustomConnectionNode = (
  node: Node,
): node is CustomConnectionNode =>
  node.type === NODE_TYPE.CUSTOM_CONNECTION_NODE;

export const isAiNode = (node: Node): node is AiNodeV2 =>
  node.type === NODE_TYPE.AI_NODE_V2;

export const isPreconditionError = (error: unknown) => {
  const response = (error as AxiosError)?.response;
  return (
    response?.status === 412 &&
    response?.data &&
    typeof response.data === "object" &&
    "detail" in response.data &&
    response.data.detail === "Precondition Failed"
  );
};

export const isConflictError = (error: unknown) => {
  const response = (error as AxiosError)?.response;
  return response?.status === 409;
};

export const isBadRequest = (
  error: unknown,
): error is AxiosError & { response: { status: 400 } } => {
  const response = (error as AxiosError)?.response;
  return response?.status === 400;
};

export const isManualReviewConnectionNode = (
  node: Node,
): node is ManualReviewNode => node.type === NODE_TYPE.REVIEW_CONNECTION_NODE;

export const isManifestConnectionNode = (
  node: Node,
): node is ManifestConnectionNode =>
  node.type === NODE_TYPE.MANIFEST_CONNECTION_NODE;

export const isFlowNode = (node: Node): node is FlowNode =>
  node.type === NODE_TYPE.FLOW_NODE;

export const isLoopNode = (node: Node): node is LoopNode =>
  node.type === NODE_TYPE.LOOP_NODE;

export const isReadEntityNode = (node: Node): node is ReadEntityNode =>
  node.type === NODE_TYPE.READ_ENTITY_NODE;

export const isWriteEntityNode = (node: Node): node is WriteEntityNode =>
  node.type === NODE_TYPE.WRITE_ENTITY_NODE;

export const isParentNode = (node: Node): node is ParentNode =>
  isFlowNode(node) || isLoopNode(node);

export const isIntegrationNodes = (node: Node): node is IntegrationNodes => {
  return (
    isIntegrationNode(node) ||
    isCustomConnectionNode(node) ||
    isInboundWebhookConnectionNode(node) ||
    isDatabaseConnectionNode(node) ||
    isManualReviewConnectionNode(node) ||
    isManifestConnectionNode(node) ||
    isAiNode(node)
  );
};

export const isDatasetMockableNode = (
  node: Node,
): node is DatasetMockableNodes => {
  return (
    isFlowNode(node) ||
    isIntegrationNodes(node) ||
    isLoopNode(node) ||
    isReadEntityNode(node)
  );
};

export const isHistoricalRunState = (
  runState: NodeRunStateV2,
): runState is NodeHistoricalRunStateV2 => {
  return (
    runState.type === "historical-data" ||
    runState.type === "historical-error" ||
    runState.type === "historical-pending"
  );
};

export const isFlowGroup = (arg: Partial<FlowGroup>): arg is FlowGroup => {
  return arg.flow !== undefined;
};

export const isFlowGroupForCount = (
  arg: Partial<FlowGroupForCount>,
): arg is FlowGroupForCount => {
  return arg.flowId !== undefined && arg.count !== undefined;
};
